// YERİ: src/app/domains/website/sections/mkt-chrome.sections.ts
// Sitenin çerçevesi: duyuru bandı + üst menü, ve alt bilgi. Ana Şablon'a (master)
// konur, böylece TÜM sayfalarda otomatik çizilir.
//
// Üst menü AÇIK zeminli. Önce koyuydu ama hero da koyuydu; artık hero ortalanmış
// ve açık (pastel mesh), koyu bir menü onun üstünde yabancı bir şerit gibi
// duruyordu. Duyuru bandı koyu kalır — sayfanın en üstünde ince bir kontrast
// çizgisi, menüyü kalabalıklaştırmadan ürün duyurusunu taşır.

import { Component, ElementRef, afterNextRender, computed, inject, input, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { LocalStorage } from '@/app/core/local-storage';
import { SiteConfig, cfg } from '@/app/domains/site/sections/section.model';
import { MktChromeStore } from '../core/chrome.store';
import { rememberLanguage } from '../core/language-preference';
import { LanguageStore } from '../core/language.store';
import { a11yLabels } from './mkt-a11y';
import { MktIcon } from './mkt-icon';
import { MktRouterLink, resolveMktLink, resolveMktFragment } from './mkt-link';
import { MktChromeText } from '../core/mkt-chrome-text';
import { isSignUpLink, MktTrialHint } from './mkt-trial-hint';

/** Pakete gömülü marka logosu — CMS'te logo alanı boşsa bu kullanılır. */
const DEFAULT_LOGO = '/melbarrel-logo.png';

/** Kapatılan duyurunun anahtarını tutan depo girdisi. */
const ANNOUNCE_DISMISS_KEY = 'mkt.announce.dismissed';

/** Metinden kısa, kararlı bir anahtar (djb2). Güvenlik amacı YOK — yalnız
    "duyuru değişti mi" sorusuna cevap verir. */
function hashText(text: string): string {
  let hash = 5381;
  for (let i = 0; i < text.length; i++) hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
  return (hash >>> 0).toString(36);
}

interface NavItem {
  label?: string;
  url?: string;
  description?: string;
  icon?: string;
  /** Düz açılır liste (iki sütun). */
  children?: NavItem[];
  /**
   * Gruplu mega menü — `children` yerine kullanılır.
   *
   * 32 sektörü tek bir düz listeye dökmek okunmaz: ziyaretçi kendi işini
   * ararken 32 satırı taramak zorunda kalır. Başlıklı sütunlar ("Sağlık",
   * "Ağırlama", "Profesyonel hizmetler") gözü doğru yere götürür.
   */
  groups?: { title?: string; items?: NavItem[] }[];

  /**
   * Menünün sağındaki öne çıkan panel — menüyü bir link listesi olmaktan
   * çıkarır. Alanlar DÜZ, iç içe nesne değil: şema motorunda 'group' alan tipi
   * yok (yalnız 'list'), nesne yapsaydık admin'den düzenlenemezdi.
   * `featureTitle` boşsa panel hiç çizilmez.
   */
  featureEyebrow?: string;
  featureTitle?: string;
  featureText?: string;
  featureCtaText?: string;
  featureCtaUrl?: string;
}

interface FooterColumn {
  title?: string;
  links?: { label?: string; url?: string }[];
}

// ═══════════════════════════════════════════════════════════════════════════
// DUYURU BANDI + ÜST MENÜ
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-navbar',
  standalone: true,
  imports: [RouterLink, MktIcon, MktTrialHint],
  host: {
    class: 'mkt-ui-type sticky top-0 z-50 block border-b border-[color:var(--mkt-border)] bg-white/85 backdrop-blur-xl',
    // Esc menüyü kapatır. Dinleyici document'ta: odak menünün dışına kaymış
    // olabilir (perdeye tıklanmış, sayfa kaydırılmış).
    '(document:keydown.escape)': 'chrome.closeAll()',
    // Dil listesi dışarı tıklanınca kapansın.
    '(document:click)': 'onDocumentClick($event)',
  },
  template: `
    <!-- ÜST ŞERİT — yalnız duyuru. Kapatılınca tamamen kaybolur.
         Dil seçici burada DEĞİL: bant kapatılabilir olduğu için, yanlış dil
         seçen ziyaretçinin geri dönecek bir yeri kalmıyordu. Seçici artık
         menü çubuğunda, "Start free" butonunun yanında — her sayfada, her
         zaman orada. -->
    @if (announceVisible()) {
      <div class="bg-[color:var(--mkt-ink)] text-white">
        <!-- Sağdaki kontroller MUTLAK konumlu, akışta değil: duyuru pili şeridin
             TAM ORTASINDA kalsın, dil seçici geldi diye sola kaymasın. -->
        <div class="relative mx-auto flex min-h-[2.25rem] max-w-7xl items-center justify-center px-24 py-1.5 sm:px-32">
          @if (announceVisible()) {
            <!-- Metin CMS'ten geliyor ve uzunluğu bilinmiyor: dar ekranda pilin
                 içinde sarılsın, taşmasın. -->
            <a [routerLink]="link(announceUrl())" [fragment]="fragment(announceUrl())"
              class="flex max-w-full items-center justify-center gap-1.5 rounded-full bg-white/10 px-3.5 py-1 text-center text-xs leading-snug transition hover:bg-white/[0.18] sm:px-4 sm:text-sm">
              <span class="text-balance">{{ announceText() }}</span>
              @if (announceCta()) {
                <span class="whitespace-nowrap font-bold">{{ announceCta() }}</span>
                <mkt-icon name="arrow" [size]="13" class="hidden shrink-0 sm:inline-flex" />
              }
            </a>
          }

          <!-- 'end-*' (right-* değil): RTL bir dilde şerit sağdan sola akar ve
               kapatma düğmesi ile dil seçici metnin BİTTİĞİ tarafta durmalı. -->
          <div class="absolute end-2 top-1/2 flex -translate-y-1/2 items-center gap-0.5 sm:end-6">
            @if (announceVisible()) {
              <button type="button" (click)="dismissAnnounce()"
                class="rounded-full p-1.5 text-white/60 transition hover:bg-white/10 hover:text-white"
                [attr.aria-label]="labels().dismissAnnouncement">
                <svg class="size-4" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="2" stroke-linecap="round" aria-hidden="true">
                  <path d="M6 6l12 12M18 6L6 18" />
                </svg>
              </button>
            }
          </div>
        </div>
      </div>
    }

    <!-- relative: gruplu mega menü li'si "static" olduğu için panel BURAYA
         göre konumlanır → nav'ın (max-w-7xl) tam genişliğini kullanır.
         mouseleave BURADA, li'de DEĞİL: panel DOM'da li'nin içinde ama nav'ın
         altına konumlanıyor; menü yalnız imleç nav ağacının TAMAMINDAN
         çıkınca kapanmalı. -->
    <nav class="relative mx-auto flex h-[68px] max-w-7xl items-center justify-between gap-3 px-4 sm:px-8 xl:gap-6"
      [attr.aria-label]="labels().mainMenu" (mouseleave)="chrome.openMenu.set(null)">

      <!-- Logo hiç küçülmez (shrink-0) — daraldığında kırpılacak şey menü
           öğeleri, marka değil. -->
      <a [routerLink]="homeLink()" (click)="chrome.closeAll()"
        class="flex shrink-0 items-center gap-2">
        <img [src]="logoUrl()" [alt]="brand()" class="h-8 w-auto sm:h-9" width="437" height="577" />
        <span class="mkt-wordmark text-[1.3125rem] leading-none sm:text-[1.4375rem]">{{ brand() }}</span>
      </a>

      <!-- Masaüstü menü. gap lg'de dar, xl'de geniş: 1024px'te 4 menü öğesi +
           sağdaki üçlü aynı satıra sığmak zorunda, yoksa üst üste biner. -->
      <ul class="hidden h-full items-center gap-5 lg:flex xl:gap-7">
        @for (item of items(); track $index) {
          <!-- Gruplu menü öğenin ALTINA ORTALANMAZ: 6 sütun bir menü başlığının
               altına sığmaz, ya sıkışır ya ekrandan taşar. O yüzden li'yi
               "static" yapıp paneli NAV'ın tam genişliğine yayıyoruz (klasik
               mega menü). Düz menüler küçük olduğu için ortalanmaya devam eder.
               ── h-full: li kutusu MENÜ ŞERİDİNİN TAMAMINI kaplar.
               İki şeyi birden çözüyor:
                 1. Boşluk tuzağı — li kutusu eskiden yalnız yazı kadardı;
                    imleç butondan aşağı inerken li'den çıkıyor, menü kapanıyor
                    ve panele hiç ulaşılamıyordu (linkler tıklanamıyordu).
                 2. Hizalama — "top-full" artık HER menüde aynı yeri gösterir
                    (şeridin altı). Öncesinde düz menüler butonun altından,
                    gruplu menüler nav'ın altından açılıyordu: farklı boşluk. -->
          <li [class]="item.groups?.length
              ? 'flex h-full items-center lg:static'
              : 'flex h-full items-center relative'"
            (mouseenter)="openMenu($index, item)">
            <!-- Açılır menü İKİ biçimden biriyle dolabilir: düz "children" ya
                 da sütun başlıklı "groups". Yalnız children'a bakmak
                 Industries'i (sadece groups var) düz linke düşürüyordu.
                 NOT: bu bir şablon literali — yorumlarda BACKTICK kullanma,
                 string'i erken kapatır ve build'i kırar. -->
            @if (item.children?.length || item.groups?.length) {
              <!-- Açılma artık salt CSS :hover DEĞİL, signal.
                   Sebebi: (1) menü açıkken arka sayfa kararmalı — perde
                   menünün dışında çizildiği için durumun paylaşılabilir olması
                   gerekiyor; (2) bir bağlantıya tıklanınca menü kapanmalı,
                   :hover ile imleç yerinde durduğu sürece kapanmaz. -->
              <button type="button" (click)="toggleMenu($index)" (focus)="openMenu($index, item)"
                [attr.aria-expanded]="chrome.openMenu() === $index"
                class="flex items-center gap-1 whitespace-nowrap text-[0.9375rem] font-semibold transition"
                [class]="chrome.openMenu() === $index
                  ? 'text-[color:var(--mkt-accent)]'
                  : 'text-[color:var(--mkt-text-muted)] hover:text-[color:var(--mkt-accent)]'">
                {{ item.label }}
                <svg class="size-4 transition-transform duration-200"
                  [class.rotate-180]="chrome.openMenu() === $index"
                  viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                  stroke-linecap="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>

              @if (chrome.openMenu() === $index) {
                <!-- pt-4: düğme ile panel arasındaki boşluk PANELE ait olmalı,
                     yoksa imleç aradan geçerken mouseleave tetiklenir ve menü
                     ziyaretçinin elinden kaçar. -->
                <!-- Düz panelin genişliği SABİT DEĞİL: sağdaki tanıtım sütunu
                     (w-80) 40rem'in yarısını yiyordu, kart başlıkları kelime
                     kelime alt alta sarılıyordu. Tanıtım sütunu yalnız xl'de
                     görünür, panel de yalnız orada genişliyor. -->
                <div class="absolute top-full pt-4"
                  [class]="item.groups?.length
                    ? 'inset-x-0'
                    : (item.featureTitle
                        ? 'left-1/2 w-[40rem] max-w-[calc(100vw-3rem)] -translate-x-1/2 xl:w-[58rem]'
                        : 'left-1/2 w-[40rem] max-w-[calc(100vw-3rem)] -translate-x-1/2')">

                  <!-- max-h + overflow: 32 sektörlü panel ile 4 satırlık panel
                       arasındaki yükseklik farkı ekranı zıplatmasın; kısa
                       ekranda (dizüstü, 1366x768) panel viewport'u aşmasın. -->
                  <div class="mkt-menu-panel flex max-h-[min(74vh,34rem)] overflow-hidden rounded-2xl border border-[color:var(--mkt-border)] bg-white shadow-2xl shadow-neutral-900/10">

                    <div class="flex-1 overflow-y-auto p-6 xl:p-7">
                      @if (item.groups?.length) {
                        <!-- Gruplu: başlıklı sütunlar. Nav genişliğini kullandığımız
                             için üç sütun rahat nefes alıyor. Sütun sayısı grup
                             sayısına göre değil KIRILMA NOKTASINA göre artar:
                             1024px'te üç sütuna sıkıştırmak başlıkları iki
                             satıra düşürüyordu. -->
                        <div class="grid gap-x-8 gap-y-5 xl:gap-x-10 xl:gap-y-6"
                          [class]="item.groups!.length >= 3 ? 'grid-cols-2 xl:grid-cols-3' : 'grid-cols-2'">
                          @for (group of item.groups; track $index) {
                            <div class="min-w-0">
                              <p class="truncate border-b border-[color:var(--mkt-border)] px-2 pb-2.5 text-[0.6875rem] font-bold uppercase tracking-wider text-[color:var(--mkt-text-faint)]">
                                {{ group.title }}
                              </p>
                              <ul class="mt-2">
                                @for (child of group.items; track $index) {
                                  <li>
                                    <a [routerLink]="link(child.url)" [fragment]="fragment(child.url)"
                                      (click)="chrome.closeAll()"
                                      class="flex items-center gap-3 rounded-lg px-2 py-1.5 text-sm font-semibold text-[color:var(--mkt-text-muted)] transition hover:bg-[color:var(--mkt-paper-soft)] hover:text-[color:var(--mkt-ink)]">
                                      @if (child.icon) {
                                        <mkt-icon [name]="child.icon" [size]="16"
                                          class="shrink-0 text-[color:var(--mkt-accent)]" />
                                      }
                                      <span class="min-w-0 truncate">{{ child.label }}</span>
                                    </a>
                                  </li>
                                }
                              </ul>
                            </div>
                          }
                        </div>
                      } @else {
                        <!-- Düz: ikon + başlık + açıklama, iki sütun -->
                        <div class="grid grid-cols-2 gap-2">
                          @for (child of item.children; track $index) {
                            <a [routerLink]="link(child.url)" [fragment]="fragment(child.url)"
                              (click)="chrome.closeAll()"
                              class="flex gap-3.5 rounded-xl p-3.5 transition hover:bg-[color:var(--mkt-paper-soft)]">
                              @if (child.icon) {
                                <span class="mt-0.5 grid size-9 shrink-0 place-items-center rounded-lg bg-[color:var(--mkt-paper-soft)] text-[color:var(--mkt-accent)]">
                                  <mkt-icon [name]="child.icon" [size]="17" />
                                </span>
                              }
                              <span class="min-w-0">
                                <span class="block text-sm font-bold">{{ child.label }}</span>
                                @if (child.description) {
                                  <span class="mt-1 block text-xs leading-relaxed text-[color:var(--mkt-text-faint)]">{{ child.description }}</span>
                                }
                              </span>
                            </a>
                          }
                        </div>
                      }
                    </div>

                    @if (item.featureTitle) {
                      <!-- Öne çıkan panel: menü bir link listesi değil, bir vitrin.
                           1280px altında GİZLİ: 320px'lik sütunu da alınca link
                           sütunları sıkışıp başlıklar sarılıyordu. -->
                      <div class="hidden w-80 shrink-0 overflow-y-auto border-s border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper-soft)] p-7 xl:block">
                        @if (item.featureEyebrow) {
                          <p class="mkt-eyebrow">{{ item.featureEyebrow }}</p>
                        }
                        <p class="mt-3 text-[0.9375rem] font-bold leading-snug">{{ item.featureTitle }}</p>
                        @if (item.featureText) {
                          <p class="mt-2.5 text-[0.8125rem] leading-relaxed text-[color:var(--mkt-text-faint)]">
                            {{ item.featureText }}
                          </p>
                        }
                        @if (item.featureCtaText) {
                          <a [routerLink]="link(item.featureCtaUrl)" (click)="chrome.closeAll()"
                            class="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[color:var(--mkt-accent)] transition-all hover:gap-2.5">
                            {{ item.featureCtaText }}
                            <mkt-icon name="arrow" [size]="14" />
                          </a>
                        }
                      </div>
                    }
                  </div>
                </div>
              }
            } @else {
              <a [routerLink]="link(item.url)" [fragment]="fragment(item.url)" (click)="chrome.closeAll()"
                class="whitespace-nowrap text-[0.9375rem] font-semibold text-[color:var(--mkt-text-muted)] transition hover:text-[color:var(--mkt-accent)]">
                {{ item.label }}
              </a>
            }
          </li>
        }
      </ul>

      <!-- Sağ blok da shrink-0: menü öğeleri uzun olduğunda esneyip "Start free"
           butonunu iki satıra kıran şey buydu. -->
      <div class="flex shrink-0 items-center gap-2 sm:gap-3">
        @if (langStore.showSwitcher()) {
          <!-- Dil seçici. Native <select> DEĞİL, kendi açılır listemiz:
               select'in açılan penceresi işletim sistemine ait, biçimlenemiyor
               ve hangi dilin seçili olduğu (tik) görünmüyordu. Burada seçili
               dil işaretli ve liste sitenin diliyle çiziliyor.

               Konumu menü çubuğu — üst şerit kapatılabilir olduğu için orada
               duramaz: yanlış dil seçen ziyaretçi geri dönemezdi. -->
          <div class="relative" #langWrap>
            <button type="button" (click)="toggleLang()"
              [attr.aria-expanded]="chrome.langOpen()" aria-haspopup="listbox"
              [attr.aria-label]="labels().language + ': ' + currentLanguageName()"
              class="flex items-center gap-1 rounded-lg px-1.5 py-1.5 text-[color:var(--mkt-text-muted)] transition hover:bg-[color:var(--mkt-paper-soft)] hover:text-[color:var(--mkt-ink)] sm:px-2">
              <mkt-icon name="globe" [size]="18" />
              <!-- Kod (EN/TR) yalnız geniş ekranda: dar ekranda küre tek başına
                   anlaşılır ve butonu şişirmez. -->
              <span class="hidden text-[0.8125rem] font-bold uppercase xl:inline">{{ langStore.current() }}</span>
              <svg class="hidden size-3.5 transition-transform duration-200 sm:block"
                [class.rotate-180]="chrome.langOpen()"
                viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"
                stroke-linecap="round" aria-hidden="true">
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>

            @if (chrome.langOpen()) {
              <!-- 'end-0' (right-0 değil): RTL bir dil seçiliyken düğme sola
                   geçer, sabit 'right-0' listeyi ekranın dışına taşırdı.
                   max-h + scroll: katalog 11 dile çıktı, liste artık ekranı aşabilir. -->
              <ul role="listbox"
                class="absolute end-0 top-full z-10 mt-2 max-h-[70vh] min-w-52 overflow-y-auto rounded-xl border border-[color:var(--mkt-border)] bg-white py-1 shadow-2xl shadow-neutral-900/10">
                @for (lang of langStore.languages(); track lang.code) {
                  <li>
                    <button type="button" role="option"
                      [attr.aria-selected]="lang.code === langStore.current()"
                      [attr.lang]="lang.code"
                      (click)="switchLanguage(lang.code)"
                      class="flex w-full items-center gap-2.5 px-3.5 py-2 text-start text-sm font-semibold transition hover:bg-[color:var(--mkt-paper-soft)]"
                      [class]="lang.code === langStore.current()
                        ? 'text-[color:var(--mkt-accent)]'
                        : 'text-[color:var(--mkt-text-muted)]'">
                      <!-- 'en-ca' beş karakter: w-8 kodu kırpıyordu. -->
                      <span class="w-12 shrink-0 text-[0.6875rem] font-bold uppercase tracking-wider text-[color:var(--mkt-text-faint)]">
                        {{ lang.code }}
                      </span>
                      <!-- Kendi dilindeki ad: "Deutsch" satırını Almanca konuşan
                           tarar, "German" satırını atlar. dir, Arapça/Farsça adın
                           listede doğru yönde çizilmesi için satır bazında verilir. -->
                      <span class="flex-1" [attr.dir]="lang.isRtl ? 'rtl' : 'ltr'">{{ lang.nativeName || lang.name }}</span>
                      @if (lang.code === langStore.current()) {
                        <mkt-icon name="check" [size]="15" class="shrink-0" />
                      }
                    </button>
                  </li>
                }
              </ul>
            }
          </div>
        }

        <!-- Giriş bağlantısı xl'den itibaren: 1024-1279px'te menü öğeleri +
             giriş + buton aynı satıra sığmıyordu. Mobil çekmecede zaten var. -->
        <a [routerLink]="['/auth/sign-in']" (click)="chrome.closeAll()"
          class="hidden whitespace-nowrap text-[0.9375rem] font-semibold text-[color:var(--mkt-text-muted)] transition hover:text-[color:var(--mkt-accent)] xl:block">
          {{ signInText() }}
        </a>

        <a [routerLink]="link(ctaUrl())" (click)="chrome.closeAll()"
          class="mkt-btn mkt-btn-primary !px-3.5 !py-2 !text-[0.8125rem] sm:!px-5 sm:!py-2.5 sm:!text-sm">
          {{ ctaText() }}
        </a>

        <button type="button" class="-mr-1 rounded-lg p-2 lg:hidden"
          [attr.aria-expanded]="chrome.mobileOpen()" aria-controls="mkt-mobile-menu"
          (click)="toggleMobile()">
          <span class="sr-only">{{ text.t('menu') }}</span>
          <svg class="size-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round" aria-hidden="true">
            @if (chrome.mobileOpen()) {
              <path d="M6 6l12 12M18 6L6 18" />
            } @else {
              <path d="M4 7h16M4 12h16M4 17h16" />
            }
          </svg>
        </button>
      </div>
    </nav>

    <!-- Mobil çekmece. max-h + scroll: 32 sektör telefonda ekranı taşırıyordu,
         alttaki "Giriş / Ücretsiz başla" hiç görünmüyordu. -->
    @if (chrome.mobileOpen()) {
      <div id="mkt-mobile-menu"
        class="max-h-[calc(100dvh-6.5rem)] overflow-y-auto overscroll-contain border-t border-[color:var(--mkt-border)] bg-white lg:hidden">
        <ul class="space-y-0.5 px-5 py-5">
          @for (item of items(); track $index) {
            <li>
              @if (item.groups?.length || item.children?.length) {
                <p class="mkt-eyebrow px-3 pb-1.5 pt-4">{{ item.label }}</p>

                <!-- Grupları da düzleştiriyoruz ama BAŞLIKLARIYLA: telefonda üç
                     sütun yok, yine de 32 sektör başlıksız bir yığın olmamalı. -->
                @for (group of item.groups ?? []; track $index) {
                  <p class="px-3 pb-1 pt-3 text-[0.6875rem] font-bold uppercase tracking-wider text-[color:var(--mkt-text-faint)]">
                    {{ group.title }}
                  </p>
                  @for (child of group.items ?? []; track $index) {
                    <a [routerLink]="link(child.url)" [fragment]="fragment(child.url)" (click)="chrome.closeAll()"
                      class="flex items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm font-semibold text-[color:var(--mkt-text-muted)] hover:bg-[color:var(--mkt-paper-soft)]">
                      @if (child.icon) {
                        <mkt-icon [name]="child.icon" [size]="15" class="shrink-0 text-[color:var(--mkt-accent)]" />
                      }
                      {{ child.label }}
                    </a>
                  }
                }

                @for (child of item.children ?? []; track $index) {
                  <a [routerLink]="link(child.url)" [fragment]="fragment(child.url)" (click)="chrome.closeAll()"
                    class="block rounded-lg px-3 py-2.5 text-sm font-semibold text-[color:var(--mkt-text-muted)] hover:bg-[color:var(--mkt-paper-soft)]">
                    {{ child.label }}
                  </a>
                }

                @if (item.featureCtaText) {
                  <a [routerLink]="link(item.featureCtaUrl)" (click)="chrome.closeAll()"
                    class="block rounded-lg px-3 py-2.5 text-sm font-bold text-[color:var(--mkt-accent)]">
                    {{ item.featureCtaText }} →
                  </a>
                }
              } @else {
                <a [routerLink]="link(item.url)" (click)="chrome.closeAll()"
                  class="block rounded-lg px-3 py-3 text-[0.9375rem] font-bold hover:bg-[color:var(--mkt-paper-soft)]">
                  {{ item.label }}
                </a>
              }
            </li>
          }
        </ul>

        <div class="flex flex-col gap-2.5 border-t border-[color:var(--mkt-border)] px-5 py-5">
          <a [routerLink]="['/auth/sign-in']" (click)="chrome.closeAll()"
            class="mkt-btn mkt-btn-secondary w-full">
            {{ signInText() }}
          </a>
          <a [routerLink]="link(ctaUrl())" (click)="chrome.closeAll()"
            class="mkt-btn mkt-btn-primary w-full">
            {{ ctaText() }}
          </a>
          @if (ctaIsSignUp()) {
            <div class="flex justify-center"><mkt-trial-hint /></div>
          }
        </div>
      </div>
    }
  `,
})
export class MktNavbarSection {
  /** Vitrin kabuk metinleri — dil `/:lang` rotasindan. */
  protected text = inject(MktChromeText);
  readonly config = input.required<SiteConfig>();

  protected langStore = inject(LanguageStore);
  protected chrome = inject(MktChromeStore);
  private storage = inject(LocalStorage);

  /** Ekran okuyucu etiketleri — sayfanın dilinde, bileşende sabit değil. */
  protected labels = computed(() => a11yLabels(this.langStore.current()));

  /**
   * Duyuru bandı kapatıldı mı.
   *
   * Başlangıç değeri HER ZAMAN false — sunucu bandı çizer, tarayıcı da aynı
   * DOM ile hidrasyona başlar. Depoyu hidrasyondan SONRA okuyoruz
   * (afterNextRender): sunucuda depo boş olduğu için baştan true yapsaydık
   * istemci ile sunucunun DOM'u ayrışır ve hidrasyon uyuşmazlığı olurdu.
   */
  protected announceDismissed = signal(false);

  protected announceVisible = computed(() => !!this.announceText() && !this.announceDismissed());

  /** Dil listesinin kabı — dışarı tıklama kontrolü için. */
  private langWrap = viewChild<ElementRef<HTMLElement>>('langWrap');

  protected brand        = computed(() => cfg(this.config(), 'brand', 'Melbarrel'));
  // Varsayılan marka logosu pakette: CMS'te logo alanı boş bırakılsa da vitrin
  // marka baş harfiyle değil GERÇEK logoyla açılır. Admin isterse üstüne yazar.
  protected logoUrl      = computed(() => cfg(this.config(), 'logoUrl', '') || DEFAULT_LOGO);
  protected ctaText      = computed(() => cfg(this.config(), 'ctaText', 'Start free'));
  protected ctaUrl       = computed(() => cfg(this.config(), 'ctaUrl', '/auth/sign-up'));
  protected ctaIsSignUp  = computed(() => isSignUpLink(this.ctaUrl()));
  protected signInText   = computed(() => cfg(this.config(), 'signInText', 'Sign in'));
  protected announceText = computed(() => cfg(this.config(), 'announceText', ''));
  protected announceCta  = computed(() => cfg(this.config(), 'announceCta', ''));
  protected announceUrl  = computed(() => cfg(this.config(), 'announceUrl', ''));

  protected items = computed<NavItem[]>(() => {
    const raw = this.config()['items'];
    return Array.isArray(raw) ? raw : [];
  });

  protected homeLink = computed(() => ['/', this.langStore.current()]);

  // ── Duyuru bandı ─────────────────────────────────────────────────────────

  /**
   * Kapatma kaydının anahtarı: duyurunun METNİNDEN türetilir.
   *
   * Sabit bir "kapatıldı" bayrağı olsaydı, ziyaretçi bir kez kapattıktan sonra
   * SONRAKİ duyuruları da hiç görmezdi. Metin değişince anahtar değişir, bant
   * yeniden açılır — yani "bu duyuruyu kapattım" demek, "duyuruları kapattım"
   * demek değil.
   */
  private announceKey = computed(() => hashText(`${this.announceText()}|${this.announceCta()}`));

  protected dismissAnnounce(): void {
    this.storage.setItem(ANNOUNCE_DISMISS_KEY, this.announceKey());
    this.announceDismissed.set(true);
  }

  constructor() {
    afterNextRender(() => {
      if (this.storage.getItem(ANNOUNCE_DISMISS_KEY) === this.announceKey()) {
        this.announceDismissed.set(true);
      }
    });
  }

  // ── Mega menü açma/kapama ────────────────────────────────────────────────

  /** Alt menüsü OLMAYAN öğe hover'landığında açık paneli kapat — imleç düz bir
      linkin üstündeyken arkada başka bir panelin açık kalması kafa karıştırır. */
  protected openMenu(index: number, item: NavItem): void {
    const hasPanel = !!(item.children?.length || item.groups?.length);
    this.chrome.openMenu.set(hasPanel ? index : null);
  }

  /** Klavye ve dokunmatik için: hover yok, tıklama aç/kapa yapar. */
  protected toggleMenu(index: number): void {
    this.chrome.openMenu.set(this.chrome.openMenu() === index ? null : index);
  }

  protected toggleMobile(): void {
    const next = !this.chrome.mobileOpen();
    this.chrome.closeAll();
    this.chrome.mobileOpen.set(next);
  }

  // ── Dil listesi ──────────────────────────────────────────────────────────

  protected toggleLang(): void {
    const next = !this.chrome.langOpen();
    this.chrome.openMenu.set(null);
    this.chrome.langOpen.set(next);
  }

  protected currentLanguageName = computed(
    () => this.langStore.currentLanguage()?.nativeName
      ?? this.langStore.currentLanguage()?.name
      ?? this.langStore.current(),
  );

  /**
   * Dışarı tıklayınca kapat.
   *
   * Hedef listenin İÇİNDEYSE dokunmuyoruz — yoksa dile tıklama, seçim
   * çalışmadan önce listeyi kapatırdı.
   */
  protected onDocumentClick(event: Event): void {
    if (!this.chrome.langOpen()) return;

    const wrap = this.langWrap()?.nativeElement;
    if (wrap && !wrap.contains(event.target as Node)) this.chrome.langOpen.set(false);
  }

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }

  /** "services#branding" → 'branding'. routerLink yol parçasını encode ettiği
      için '#' AYRI verilmek zorunda (yoksa %23 olur ve 404 döner). */
  protected fragment(url?: string): string | undefined {
    return resolveMktFragment(url ?? '');
  }

  /**
   * Dil değiştir. Navbar sayfanın o dildeki karşılığını (alternates) bilmiyor —
   * bu yüzden AYNI yola geçiyoruz. Sayfa o dilde yoksa 404 görülür; bu doğru:
   * var olmayan bir çeviriye link VERMİYORUZ, ziyaretçi bilinçli değiştirdi.
   *
   * Seçim ÇEREZE yazılır: bu ziyaretçinin AÇIK tercihidir ve bundan sonra
   * tarayıcı dilini ezer. Kanada'da yaşayan, tarayıcısı İngilizce ama Farsça
   * okumayı tercih eden biri bir kez seçer; melbarrel.com'a çıplak adresle
   * döndüğünde de Farsça karşılanır.
   *
   * Tam sayfa yenileme (router değil): SSR yeni dilin HTML'ini baştan üretsin.
   */
  protected switchLanguage(code: string): void {
    rememberLanguage(code);

    const segments = window.location.pathname.split('/').filter(Boolean);
    segments[0] = code;
    window.location.assign(`/${segments.join('/')}`);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// ALT BİLGİ
// ═══════════════════════════════════════════════════════════════════════════

@Component({
  selector: 'mkt-footer',
  standalone: true,
  imports: [RouterLink],
  host: { class: 'block border-t border-[color:var(--mkt-border)] bg-[color:var(--mkt-paper)]' },
  template: `
    <footer class="mx-auto max-w-7xl px-5 py-16 sm:px-8">

      @if (contactText()) {
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-[color:var(--mkt-border)] pb-8">
          <span class="font-bold">{{ contactText() }}</span>
          @if (contactEmail()) {
            <a [href]="'mailto:' + contactEmail()" class="font-semibold text-[color:var(--mkt-accent)] underline">
              {{ contactEmail() }}
            </a>
          }
        </div>
      }

      <div class="mt-12 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        @for (column of columns(); track $index) {
          <div>
            <h3 class="text-base font-extrabold">{{ column.title }}</h3>
            <ul class="mt-5 space-y-3">
              @for (item of (column.links || []); track $index) {
                <li>
                  <a [routerLink]="link(item.url)" [fragment]="fragment(item.url)"
                    class="text-[0.9375rem] text-[color:var(--mkt-text-muted)] transition hover:text-[color:var(--mkt-accent)]">
                    {{ item.label }}
                  </a>
                </li>
              }
            </ul>
          </div>
        }
      </div>

      <div class="mt-14 flex flex-col items-center justify-between gap-4 border-t border-[color:var(--mkt-border)] pt-8 sm:flex-row">
        <div class="flex flex-col items-center gap-1.5 sm:items-start">
          <div class="flex items-center gap-2.5">
            <img [src]="logoUrl()" [alt]="brand()" class="h-7 w-auto" width="437" height="577" />
            <p class="text-sm text-[color:var(--mkt-text-faint)]">
              © {{ year }} {{ brand() }}. {{ rightsText() }}
            </p>
          </div>
          <!-- Slogan panelde bir alan olarak duruyordu ama hiç çizilmiyordu
               (ölü ayar). Boşsa satır hiç çizilmez. -->
          @if (tagline()) {
            <p class="max-w-md text-center text-sm text-[color:var(--mkt-text-faint)] sm:text-start">{{ tagline() }}</p>
          }
        </div>

        <ul class="flex flex-wrap gap-x-6 gap-y-2">
          <!-- Sosyal hesaplar DIŞ bağlantı: routerLink değil düz href, yeni sekme. -->
          @for (social of socials(); track $index) {
            @if (social.url) {
              <li>
                <a [href]="social.url" target="_blank" rel="noopener noreferrer"
                  class="text-sm font-semibold text-[color:var(--mkt-text-faint)] transition hover:text-[color:var(--mkt-accent)]">
                  {{ social.label || social.url }}
                </a>
              </li>
            }
          }
          @for (item of legalLinks(); track $index) {
            <li>
              <a [routerLink]="link(item.url)"
                class="text-sm text-[color:var(--mkt-text-faint)] transition hover:text-[color:var(--mkt-accent)]">
                {{ item.label }}
              </a>
            </li>
          }
        </ul>
      </div>
    </footer>
  `,
})
export class MktFooterSection {
  readonly config = input.required<SiteConfig>();

  protected langStore = inject(LanguageStore);
  protected year = new Date().getFullYear();

  protected brand        = computed(() => cfg(this.config(), 'brand', 'Melbarrel'));
  protected logoUrl      = computed(() => cfg(this.config(), 'logoUrl', '') || DEFAULT_LOGO);
  protected rightsText   = computed(() => cfg(this.config(), 'rightsText', 'All rights reserved.'));
  protected tagline      = computed(() => cfg(this.config(), 'tagline', ''));

  /** Yalnız http(s) adresleri — panelden girilen değer href'e doğrudan gider. */
  protected socials = computed<{ label?: string; url?: string }[]>(() => {
    const raw = this.config()['socials'];
    const list: { label?: string; url?: string }[] = Array.isArray(raw) ? raw : [];
    return list.filter((s) => /^https?:\/\//i.test((s?.url ?? '').trim()));
  });
  protected contactText  = computed(() => cfg(this.config(), 'contactText', ''));
  protected contactEmail = computed(() => cfg(this.config(), 'contactEmail', ''));

  protected columns = computed<FooterColumn[]>(() => {
    const raw = this.config()['columns'];
    return Array.isArray(raw) ? raw : [];
  });

  protected legalLinks = computed<{ label?: string; url?: string }[]>(() => {
    const raw = this.config()['legalLinks'];
    return Array.isArray(raw) ? raw : [];
  });

  protected link(url?: string): MktRouterLink {
    return resolveMktLink(url ?? '', this.langStore.current());
  }

  /** "services#branding" → 'branding'. routerLink yol parçasını encode ettiği
      için '#' AYRI verilmek zorunda (yoksa %23 olur ve 404 döner). */
  protected fragment(url?: string): string | undefined {
    return resolveMktFragment(url ?? '');
  }
}
